import { type ClientId } from './client/bridge-api-client-types';
import { RovoContentBridgeAPI } from './rovo-bridge-api';
import { clearAPI, mountAPI, readAPI } from './rovo-bridge-api-browser';
import { BrowserTransport } from './transport/transports/browser-transport';

/**
 * A singleton instance to the Rovo bridge API, for bidirectional communication between Rovo Chat and fully immersive content
 * representations. Available in the scope of a single JavaScript context.
 *
 * @description For browser use cases, use {@link getBrowserBridgeAPI}.
 *
 * @description Note: A content frame in this context can refer to either (1) preview of content in a 'staging' view (
 * see @atlassian/smart-creation), or (2) the content actually rendered in an application (e.g. a Confluence page).
 *
 * @description Note: Here, a single JavaScript context can mean any of the following scenarios:
 * - a web application and React component mounted inside of it (e.g. Confluence + Confluence Pages)
 * - a web application and same-origin `<iframe>` mounted inside of it (e.g. Confluence + Confluence Whiteboards / Databases)
 *
 * The following cases represent distinct JavaScript contexts:
 * - a web application and a cross-origin `<iframe>` mounted inside of it (e.g. a third-party Smart Link embed)
 * - the main thread and a worker thread in a web application
 * - the main thread and renderer thread in a desktop application
 *
 * In the case that you have a distinct JavaScript context, ensure you use a transport protocol optimised for your use case.
 * @see {@link getBrowserBridgeAPI} for usage in a browser context.
 */
let bridgeApiInstance: RovoContentBridgeAPI | null = null;

/**
 * Options for creating the browser bridge API singleton.
 */
export type BrowserTargetResolver = (args: {
	receiverId: ClientId;
	source: string;
}) => Window | undefined;

export type BrowserBridgeAPIOptions = {
	/**
	 * Resolve a browser window target for a receiver before falling back to the default browser
	 * target resolution strategy.
	 *
	 * @description This option is only used on first creation. If a singleton bridge API already exists,
	 * or one is already mounted on the topmost same-origin window, the existing instance is returned.
	 */
	resolveTarget?: BrowserTargetResolver;

	/**
	 * Exact origins permitted when a custom target resolver cannot provide origin metadata.
	 * Values must come from trusted host configuration, never from a URL or message payload.
	 */
	allowedOrigins?: readonly string[];
};

/**
 * Provides a singleton instance to the Rovo bridge API, for bidirectional communication between Rovo Chat and fully
 * immersive content reprsentations in a browser context.
 *
 * @description In same-origin contexts, {@link window.top.__rovoContentBridgeAPI__} is used as a shared reference.
 * This is to optimise execution, avoiding the need for asynchronous message passing, and complex stack frames.
 *
 * @description In cross-origin contexts, the {@link BrowserTransport} protocol is used to exchange messages
 * between contexts asynchronously, and handle various scenarios in a safe manner (differences in mount time, etc).
 *
 * @returns a bridge API instance from either global state, window.top, or freshly constructed.
 */
export const getBrowserBridgeAPI = (
	options: BrowserBridgeAPIOptions = {},
): RovoContentBridgeAPI => {
	// 1) read singleton instance
	if (bridgeApiInstance) {
		return bridgeApiInstance;
	}

	// 2) read singleton instance from window.top
	const api = readAPI();
	if (api) {
		return api;
	}

	// 3) create and yield
	const source = window.origin;
	bridgeApiInstance = new RovoContentBridgeAPI(
		source,
		new BrowserTransport(source, {
			resolveTarget: options.resolveTarget,
			allowedOrigins: options.allowedOrigins,
		}),
	);
	mountAPI(bridgeApiInstance);

	return bridgeApiInstance;
};

/**
 * Clears an application-wide singleton reference to Rovo content API.
 *
 * @description Use this to ensure garbage collection takes place when unmounting an application context
 * which leverages the bridge API. Note: Most JS engines will do this automagically if there are no references
 * to the {@link bridgeApiInstance} singleton when an app undergoes destruction. If your application references
 * the bridge API in unique ways (e.g. in a non-React application), you may want to utilize this for the purpose
 * of assuring GC takes place.
 *
 * @description Example usage: in the Confluence Whiteboards application, which is not a React application, for
 * explicit lifecycle and memory management.
 */
export const destroyBridgeAPI = (): void => {
	// Ensure bridge instance is fully destroyed first to clean up all internal state
	if (bridgeApiInstance) {
		bridgeApiInstance.destroy();
	}

	bridgeApiInstance = null;
	clearAPI();
};
